import * as R from 'ramda'
import Bowser from 'bowser'
import debounce from 'lodash.debounce'

export const debouncedFocus = debounce(el => el.focus(), 30)

// ==========================================
// Detect browser rendering (not SSR)
// ==========================================
export const isBrowser = typeof window === 'object'

// ==========================================
// Detect rendering in iframe
// ========================
export const inIframe = () => {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

// ==========================================
// Detect Storage Access Api
// ========================
export const hasTrackingPrevention = () => {
  if (isBrowser) {
    const browser = Bowser.getParser(window.navigator.userAgent)
    return browser.satisfies({
      safari: '>0'
    })
  }

  return false
}

// ==========================================
// Vertically scrolls a child element into view inside its parent
//    NOTE: Parent should have position set to something other than `static`
//      so child offsetTop can be calculated correctly
// ==========================================
export const scrollIntoView = (container, child) => {
  const viewHeight = container.clientHeight
  const viewTop = container.scrollTop
  const viewBottom = viewTop + viewHeight
  const itemTop = child.offsetTop
  const itemBottom = itemTop + child.offsetHeight

  if (viewTop > itemTop) {
    container.scrollTop = itemTop
  } else if (viewBottom < itemBottom) {
    container.scrollTop = itemBottom - viewHeight
  }
}

// ==========================================
// Detect whether the screen is a retina screen or not
// ==========================================
export const isRetina = () => {
  return window.devicePixelRatio >= 2
}

// ==========================================
// Polyfill for non-RAF browsers
// ==========================================
export const getAnimationFrame = () => {
  if (!window.requestAnimationFrame) {
    window.requestAnimationFrame =
      window.webkitRequestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      window.oRequestAnimationFrame ||
      window.msRequestAnimationFrame ||
      function (cb) {
        return window.setTimeout(cb, 1000 / 60)
      }
  }
}

// ==========================================
// Load a virtual image
// ==========================================
export const loadImg = (src, callback) => {
  const newImg = new Image()

  newImg.setAttribute('crossOrigin', 'anonymous')
  newImg.onload = () => {
    if (typeof callback === 'function') {
      callback(newImg)
    }
  }
  newImg.src = decodeURIComponent(src)
}

// ==========================================
// Node.contains, and include top-level node
// ==========================================
export const contains = R.curryN(2, (node, target) => {
  return R.is(Node, node) && (node === target || node.contains(target))
})

export const isInView = boundingRect => {
  const { top, bottom, height } = boundingRect
  const viewportHeight = window.innerHeight

  const overflow = Math.max(0, -top) + Math.max(0, bottom - viewportHeight)
  const amountInViewport = height - overflow
  const isInViewportMajority =
    amountInViewport > viewportHeight - amountInViewport
  const isMostlyInView = amountInViewport * 0.5 > overflow

  return !overflow || isInViewportMajority || isMostlyInView
}

export const getImageSize = (src, callback, timeOut = 1000) => {
  const newImg = new Image()

  let timer = null
  let timedOut = false

  const onErrorAbort = () => {
    if (!timedOut) {
      clearTimeout(timer)
      callback({ isImg: false, status: 'failed' })
    }
  }

  newImg.onerror = onErrorAbort
  newImg.onabort = onErrorAbort

  newImg.onload = () => {
    if (!timedOut) {
      clearTimeout(timer)
      callback({
        w: newImg.width,
        h: newImg.height,
        isImg: true,
        status: 'success'
      })
    }
  }

  timer = setTimeout(function () {
    timedOut = true
    newImg.src = '//!!!!/test.jpg'
    callback({ isImg: false, status: 'timeout' })
  }, timeOut)

  newImg.src = src
}
